import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.avif";

const services = [
  { name: "Pressure Washing", href: "/services/pressure-washing" },
  { name: "House Washing", href: "/services/house-washing" },
  { name: "Roof Washing / Soft Washing", href: "/services/roof-washing" },
  { name: "Concrete Cleaning & Sealing", href: "/services/concrete-cleaning" },
  { name: "Driveway Cleaning", href: "/services/driveway-cleaning" },
  { name: "Deck Cleaning & Restoration", href: "/services/deck-cleaning" },
  { name: "Patio Cleaning", href: "/services/patio-cleaning" },
  { name: "Sidewalk Cleaning", href: "/services/sidewalk-cleaning" },
  { name: "Gutter Cleaning", href: "/services/gutter-cleaning" },
  { name: "Brick & Paver Cleaning", href: "/services/brick-paver-cleaning" },
  { name: "Rust & Stain Removal", href: "/services/rust-stain-removal" },
  { name: "Vinyl Fence Cleaning", href: "/services/vinyl-fence-cleaning" },
  { name: "Wood Restoration", href: "/services/wood-restoration" },
  { name: "Oxidation Removal", href: "/services/oxidation-removal" },
  { name: "Commercial Services", href: "/services/commercial" },
];

const serviceAreas = [
  { name: "Inwood, WV", href: "/service-areas/inwood-wv" },
  { name: "Martinsburg, WV", href: "/service-areas/martinsburg-wv" },
  { name: "Falling Waters, WV", href: "/service-areas/falling-waters-wv" },
  { name: "Hedgesville, WV", href: "/service-areas/hedgesville-wv" },
  { name: "Berkeley Springs, WV", href: "/service-areas/berkeley-springs-wv" },
  { name: "Charles Town, WV", href: "/service-areas/charles-town-wv" },
  { name: "Ranson, WV", href: "/service-areas/ranson-wv" },
  { name: "Kearneysville, WV", href: "/service-areas/kearneysville-wv" },
  { name: "Winchester, VA", href: "/service-areas/winchester-va" },
  { name: "Harpers Ferry, WV", href: "/service-areas/harpers-ferry-wv" },
  { name: "Bunker Hill, WV", href: "/service-areas/bunker-hill-wv" },
  { name: "Shepherdstown, WV", href: "/service-areas/shepherdstown-wv" },
  { name: "Berryville, VA", href: "/service-areas/berryville-va" },
  { name: "Stephens City, VA", href: "/service-areas/stephens-city-va" },
];

const aboutItems = [
  { name: "About Us", href: "/about" },
  { name: "Residential", href: "/services" },
  { name: "Commercial", href: "/commercial" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.dropdown-container')) setOpenDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleDropdown = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const handleDropdownEnter = (name: string) => setOpenDropdown(name);
  const handleDropdownLeave = () => setOpenDropdown(null);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg py-2" : "bg-white py-3"}`}>
      <div className="container-custom">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex-shrink-0">
            <img src={logo} alt="Stream Masters Power Washing" width={200} height={80} className={`transition-all duration-300 w-auto ${isScrolled ? "h-14 md:h-16" : "h-16 md:h-20"}`} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link to="/" className="px-3 py-2 text-foreground hover:text-primary font-medium transition-colors text-sm">Home</Link>

            <div className="relative dropdown-container" onMouseEnter={() => handleDropdownEnter("services")} onMouseLeave={handleDropdownLeave}>
              <button className="px-3 py-2 text-foreground hover:text-primary font-medium transition-colors flex items-center gap-1 text-sm" onClick={(e) => toggleDropdown("services", e)} aria-expanded={openDropdown === "services"} aria-haspopup="true">
                Services <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "services" ? "rotate-180" : ""}`} />
              </button>
              <div className={`absolute top-full left-0 w-60 max-h-[70vh] overflow-y-auto bg-white shadow-xl rounded-lg py-2 transition-all duration-200 ${openDropdown === "services" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"}`}>
                <Link to="/services" className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors font-medium border-b border-border">All Services</Link>
                {services.map((service) => (
                  <Link key={service.href} to={service.href} className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors text-sm">{service.name}</Link>
                ))}
              </div>
            </div>

            <div className="relative dropdown-container" onMouseEnter={() => handleDropdownEnter("areas")} onMouseLeave={handleDropdownLeave}>
              <button className="px-3 py-2 text-foreground hover:text-primary font-medium transition-colors flex items-center gap-1 text-sm" onClick={(e) => toggleDropdown("areas", e)} aria-expanded={openDropdown === "areas"} aria-haspopup="true">
                Service Areas <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "areas" ? "rotate-180" : ""}`} />
              </button>
              <div className={`absolute top-full left-0 w-52 bg-white shadow-xl rounded-lg py-2 transition-all duration-200 ${openDropdown === "areas" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"}`}>
                {serviceAreas.map((area) => (
                  <Link key={area.href} to={area.href} className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors text-sm">{area.name}</Link>
                ))}
              </div>
            </div>

            <Link to="/gallery" className="px-3 py-2 text-foreground hover:text-primary font-medium transition-colors text-sm">Portfolio</Link>

            <div className="relative dropdown-container" onMouseEnter={() => handleDropdownEnter("about")} onMouseLeave={handleDropdownLeave}>
              <button className="px-3 py-2 text-foreground hover:text-primary font-medium transition-colors flex items-center gap-1 text-sm" onClick={(e) => toggleDropdown("about", e)} aria-expanded={openDropdown === "about"} aria-haspopup="true">
                About <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "about" ? "rotate-180" : ""}`} />
              </button>
              <div className={`absolute top-full left-0 w-44 bg-white shadow-xl rounded-lg py-2 transition-all duration-200 ${openDropdown === "about" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"}`}>
                {aboutItems.map((item) => (
                  <Link key={item.href} to={item.href} className="block px-4 py-2 text-foreground hover:bg-muted hover:text-primary transition-colors text-sm">{item.name}</Link>
                ))}
              </div>
            </div>

            <Link to="/contact" className="px-3 py-2 text-foreground hover:text-primary font-medium transition-colors text-sm">Contact</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="https://www.facebook.com/share/17qEwQT9Fd/?mibextid=wwXlfr" target="_blank" rel="noopener noreferrer" className="p-2 text-foreground hover:text-primary transition-colors" aria-label="Visit our Facebook page">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="tel:+12762123137" className="flex items-center gap-2 px-3 py-2 text-primary font-semibold hover:text-primary/80 transition-all text-sm">
              <Phone className="w-4 h-4" />
              (276) 212-3137
            </a>
            <Link to="/contact">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-5 shadow-lg hover:shadow-xl transition-all text-sm">GET FREE QUOTE</Button>
            </Link>
          </div>

          <button className="lg:hidden p-2 text-foreground min-w-[48px] min-h-[48px] flex items-center justify-center" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"} aria-expanded={isMobileMenuOpen}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${isMobileMenuOpen ? "max-h-screen opacity-100 visible" : "max-h-0 opacity-0 invisible"}`}>
          <nav className="py-4 space-y-2">
            <Link to="/" className="block px-4 py-3 text-foreground hover:text-primary font-medium min-h-[48px] flex items-center">Home</Link>

            <div>
              <button className="w-full flex items-center justify-between px-4 py-3 text-foreground font-medium min-h-[48px]" onClick={(e) => toggleDropdown("mobile-services", e)} aria-expanded={openDropdown === "mobile-services"} aria-haspopup="true">
                Services <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "mobile-services" ? "rotate-180" : ""}`} />
              </button>
              <div className={`pl-4 space-y-1 overflow-hidden transition-all ${openDropdown === "mobile-services" ? "max-h-[600px]" : "max-h-0"}`}>
                <Link to="/services" className="block px-4 py-3 text-foreground hover:text-primary min-h-[48px] flex items-center">All Services</Link>
                {services.map((service) => (
                  <Link key={service.href} to={service.href} className="block px-4 py-3 text-muted-foreground hover:text-primary min-h-[48px] flex items-center">{service.name}</Link>
                ))}
              </div>
            </div>

            <div>
              <button className="w-full flex items-center justify-between px-4 py-3 text-foreground font-medium min-h-[48px]" onClick={(e) => toggleDropdown("mobile-areas", e)} aria-expanded={openDropdown === "mobile-areas"} aria-haspopup="true">
                Service Areas <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "mobile-areas" ? "rotate-180" : ""}`} />
              </button>
              <div className={`pl-4 space-y-1 overflow-hidden transition-all ${openDropdown === "mobile-areas" ? "max-h-[600px]" : "max-h-0"}`}>
                {serviceAreas.map((area) => (
                  <Link key={area.href} to={area.href} className="block px-4 py-3 text-muted-foreground hover:text-primary min-h-[48px] flex items-center">{area.name}</Link>
                ))}
              </div>
            </div>

            <Link to="/gallery" className="block px-4 py-3 text-foreground hover:text-primary font-medium min-h-[48px] flex items-center">Portfolio</Link>

            <div>
              <button className="w-full flex items-center justify-between px-4 py-3 text-foreground font-medium min-h-[48px]" onClick={(e) => toggleDropdown("mobile-about", e)} aria-expanded={openDropdown === "mobile-about"} aria-haspopup="true">
                About <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === "mobile-about" ? "rotate-180" : ""}`} />
              </button>
              <div className={`pl-4 space-y-1 overflow-hidden transition-all ${openDropdown === "mobile-about" ? "max-h-40" : "max-h-0"}`}>
                {aboutItems.map((item) => (
                  <Link key={item.href} to={item.href} className="block px-4 py-3 text-muted-foreground hover:text-primary min-h-[48px] flex items-center">{item.name}</Link>
                ))}
              </div>
            </div>

            <Link to="/contact" className="block px-4 py-3 text-foreground hover:text-primary font-medium min-h-[48px] flex items-center">Contact</Link>

            <div className="pt-4 space-y-3 px-4">
              <a href="tel:+12762123137" className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-primary text-primary rounded-lg font-semibold min-h-[48px]">
                <Phone className="w-4 h-4" />
                (276) 212-3137
              </a>
              <Link to="/contact" className="block">
                <Button className="w-full bg-primary text-primary-foreground font-bold py-3 min-h-[48px]">GET FREE QUOTE</Button>
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;